import { INITIAL_FACET_OVERRIDES } from '@/pages/Search/SearchPage/types';
import { SearchExclusionSlice } from '@/redux/modules/search/exclusions/searchExclusions.types';
import { SearchUrlTrigger } from '@/types/search/enums/searchUrlTrigger';
import {
    AugmentedSearchQuery,
    AugmentedSearchQueryOption,
    AugmentedSearchQueryOptions,
    SearchQuery,
} from '@/types/search/searchQuery';
import { SearchCompletedTrigger } from '@/utils/amplitude/analytics/core.types';

const emptyAuctionHouseFacet: AugmentedSearchQueryOption = { exclude: [], include: [] };

/**
 * Build the `options` block search-party expects: each facet with user-defined exclusions becomes
 * `[{ exclude, include }]`, and any page-level auction house exclusions are unioned onto
 * `auctionHouse`.
 *
 * Shared by the organic search path and `searchPromoted`, which used to carry its own copy of this
 * logic -- so a sponsored lot from an excluded house could still surface on a page that excluded
 * that house.
 */
export const getAugmentedSearchOptions = (
    options: SearchQuery['options'],
    searchExclusions: SearchExclusionSlice['excludedFacets'],
    pageExcludedAuctionHouseIds: string[] = []
): AugmentedSearchQueryOptions => {
    const augmentedOptions: AugmentedSearchQueryOptions = Object.keys(searchExclusions).reduce(
        (acc, facetWithExclusions) => ({
            ...acc,
            [facetWithExclusions]: [
                {
                    exclude: searchExclusions[facetWithExclusions] || [],
                    include: options?.[facetWithExclusions] || [],
                },
            ],
        }),
        {
            ...options,
            auctionHouse: [emptyAuctionHouseFacet],
        }
    );

    if (pageExcludedAuctionHouseIds.length) {
        const [houseFacet] = augmentedOptions.auctionHouse ?? [emptyAuctionHouseFacet];
        augmentedOptions.auctionHouse = [
            {
                ...houseFacet,
                exclude: Array.from(new Set([...houseFacet.exclude, ...pageExcludedAuctionHouseIds])),
            },
        ];
    }

    return augmentedOptions;
};

export const getAugmentedSearchQuery = (
    searchQuery: SearchQuery,
    searchExclusions: SearchExclusionSlice['excludedFacets']
): AugmentedSearchQuery => {
    // `excludedAuctionHouseIds` is a frontend-only field; it is folded into `options` and must
    // never reach the wire.
    const { excludedAuctionHouseIds, ...queryForRequest } = searchQuery;

    const augmentedSearchQuery: AugmentedSearchQuery = {
        ...queryForRequest,
        // INITIAL_FACET_OVERRIDES first so any caller-supplied facetOverrides
        // win on the same key -- View More refetches via
        // `refetchWithFacetOverrides` carry their own size=100/500 for the
        // expanded facet and must override the initial size=6/7.
        facetOverrides: { ...INITIAL_FACET_OVERRIDES, ...searchQuery.facetOverrides },
        options: getAugmentedSearchOptions(searchQuery.options, searchExclusions, excludedAuctionHouseIds),
    };

    augmentedSearchQuery.topRated = augmentedSearchQuery.options.topRated;

    return augmentedSearchQuery;
};

export function getSearchCompletedTrigger(
    trigger: string | undefined,
    searchSuggestionUsed: boolean,
    submittedFromSearchBar: boolean
): SearchCompletedTrigger {
    if (trigger === SearchUrlTrigger.UpcomingItems) {
        return SearchCompletedTrigger.UpcomingItems;
    }
    if (searchSuggestionUsed || submittedFromSearchBar) {
        return SearchCompletedTrigger.SearchBar;
    }
    return SearchCompletedTrigger.Other;
}
